import AICampusFooter from '@/components/probaworkflows_base_components/AICampusFooter/AiFooter.vue';
import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import {
  FIRST_TIME_LOGIN_URL,
  GET_APPLICATION_FORMS,
  GET_TABS_DETAILS,
  GET_USER_PROFILE_DETAILS_URL,
  SUBMIT_APPLICATION_FORMS_ANSWERS,
  UPDATE_USER_URL,
  get_headers,
} from '@/utils/constants/constants';
import {
  get_language_proficiency_options,
  get_official_language_options,
  map_language_selection_value,
  normalize_language_proficiency_value,
} from '@/utils/helpers/language_card_options';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { parse_single_field_phone_input, validate_phone } from '@/utils/validations/contact_validators';
import axios from 'axios';

function get_last_path_segment(path) {
  const segments = String(path || '').split('/');
  return segments.pop() || '';
}

function get_file_name_from_url(url) {
  const raw_url = String(url || '').trim();
  if (!raw_url) return '';

  if (typeof URL.canParse === 'function' && URL.canParse(raw_url)) {
    return decodeURIComponent(get_last_path_segment(new URL(raw_url).pathname));
  }

  const sanitized_url = raw_url.split('?')[0];
  return decodeURIComponent(get_last_path_segment(sanitized_url));
}

function looks_like_url(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  return text.startsWith('http://') || text.startsWith('https://') || text.startsWith('/media/') || text.startsWith('blob:') || text.includes('.s3.');
}

function parse_json_or_null(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extract_file_answer(raw_answer, depth = 0) {
  if (depth > 4 || raw_answer === null || raw_answer === undefined) {
    return null;
  }

  if (typeof raw_answer === 'string') {
    const trimmed = raw_answer.trim();
    if (!trimmed) return null;

    if (looks_like_url(trimmed)) {
      return {
        url: trimmed,
        name: get_file_name_from_url(trimmed) || trimmed,
      };
    }

    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return extract_file_answer(JSON.parse(trimmed), depth + 1);
      } catch {
        return {
          url: null,
          name: trimmed,
        };
      }
    }

    return {
      url: null,
      name: trimmed,
    };
  }

  if (Array.isArray(raw_answer)) {
    for (const item of raw_answer) {
      const extracted = extract_file_answer(item, depth + 1);
      if (extracted) return extracted;
    }
    return null;
  }

  if (typeof raw_answer === 'object') {
    const nested = extract_file_answer(
      Reflect.get(raw_answer, 'answer') ?? Reflect.get(raw_answer, 'answer_text'),
      depth + 1
    );
    if (nested) return nested;

    const url = Reflect.get(raw_answer, 'presigned_url')
      || Reflect.get(raw_answer, 'original_url')
      || Reflect.get(raw_answer, 'file_url')
      || Reflect.get(raw_answer, 'url')
      || null;

    const name = Reflect.get(raw_answer, 'file_name')
      || Reflect.get(raw_answer, 'name')
      || get_file_name_from_url(url)
      || '';

    if (url || name) {
      return {
        url: url || null,
        name,
      };
    }
  }

  return null;
}

function normalize_answer_value(raw_answer) {
  if (raw_answer === null || raw_answer === undefined || raw_answer === '') {
    return 'Not provided';
  }

  const file_answer = extract_file_answer(raw_answer);
  if (file_answer?.url) {
    return file_answer.url;
  }
  if (file_answer?.name) {
    return file_answer.name;
  }

  if (Array.isArray(raw_answer)) {
    if (!raw_answer.length) {
      return 'Not provided';
    }
    return raw_answer
      .map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)))
      .join(', ');
  }

  if (typeof raw_answer === 'object') {
    if (raw_answer.answer !== undefined) {
      return normalize_answer_value(raw_answer.answer);
    }
    if (raw_answer.answer_text !== undefined) {
      return normalize_answer_value(raw_answer.answer_text);
    }

    const keys = Object.keys(raw_answer);
    if (!keys.length) {
      return 'Not provided';
    }
    return JSON.stringify(raw_answer);
  }

  return String(raw_answer);
}

function parse_multi_select_answer(raw_answer) {
  if (!raw_answer) return [];
  if (Array.isArray(raw_answer)) {
    return raw_answer.map((item) =>
      typeof item === 'object' ? (item.choice || item.answer || String(item)) : String(item),
    );
  }
  if (typeof raw_answer === 'object') {
    if (raw_answer.answer !== undefined) return parse_multi_select_answer(raw_answer.answer);
    if (raw_answer.answer_text !== undefined) return parse_multi_select_answer(raw_answer.answer_text);
    return [];
  }
  if (typeof raw_answer === 'string' && raw_answer.trim()) {
    return raw_answer.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export default {
  name: 'ProfileInfo',
  mixins: [alert_mixin],
  props: {
    form_source: {
      type: String,
      default: 'profile',
    },
    application_id: {
      type: [String, Number],
      default: null,
    },
    entity_profile_id: {
      type: [String, Number],
      default: null,
    },
    entity_form_main_section_ids: {
      type: Array,
      default: () => [],
    },
    entity_application_form_id: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    AICampusFooter,
    FilePreviewComponent,
  },
  emits: ['profile-completion'],
  data: () => ({
    headers: {},
    is_editing: false,
    show_page_help: false,
    is_loading: false,
    is_saving: false,
    current_section_index: 0,
    save_message: 'Unsaved changes',
    profile_title: 'Profile',
    profile_form_id: null,
    password_input_type: 'password',
    confirm_password_input_type: 'password',
    new_password: '',
    confirm_password: '',
    account_details: {
      full_name: 'User',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date_joined: '',
    },
    profile_questions: [],
    hidden_synced_questions: [],
    identity_defaults: {},
    has_profile_form: true,
    stats: { applied: 0, interviewed: 0, withdrawn: 0 },
    profile_sections: [],
    work_experience_blocks: [],
    file_preview_modal: false,
    file_preview_url: null,
    file_preview_name: 'Document',
    profile_validation_errors: {},
    profile_completion_emit_timer: null,
  }),
  computed: {
    is_entity_form_mode() {
      return String(this.form_source || '').toLowerCase() === 'entity';
    },

    profile_flow_sections() {
      const sections = [];
      // Only hide sections on the profile tab view (form_source === 'profile')
      const hidden_section_titles = this.form_source === 'profile'
        ? ['THE ADVERTISED POST', 'Z83 DECLARATION',
          'THE ADVERTISED POST (All sections of this form are compulsory)'
        ]
        : [];

      if (!this.is_entity_form_mode) {
        sections.push({
          key: 'account',
          kind: 'account',
          title: 'Account Details',
          description: 'Update your core profile information before moving through the remaining sections.',
        });
      }

      (this.profile_sections || []).forEach((section, index) => {
        const section_title = section.name || `Section ${index + 1}`;
        // Skip sections with hidden titles (only when on profile tab)
        if (hidden_section_titles.includes(section_title.toUpperCase())) {
          return;
        }
        sections.push({
          key: section.id || `section-${index + 1}`,
          kind: section.kind || 'section',
          title: section_title,
          description: section.description || 'Complete the questions in this section to keep your profile current.',
          questions: section.questions || [],
          blocks: section.blocks || [],
          all_blocks: section.all_blocks || [],
        });
      });

      if ((this.work_experience_blocks || []).length) {
        sections.push({
          key: 'work-experience',
          kind: 'work',
          title: 'Work Experience',
          description: 'Review your work history and keep the latest details up to date.',
        });
      }

      return sections;
    },

    current_profile_section() {
      return this.profile_flow_sections[this.current_section_index] || null;
    },

    user_initials() {
      const first = String(this.account_details.first_name || '').trim();
      const last = String(this.account_details.last_name || '').trim();
      const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
      if (initials) return initials;
      const full_name = String(this.account_details.full_name || '').trim();
      if (!full_name) return 'U';
      return full_name.split(' ').filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || 'U';
    },

    profile_completion_percent() {
      // Only count mandatory/required fields
      const mandatory_identity_fields = this.is_entity_form_mode
        ? []
        : [
          this.account_details.first_name,
          this.account_details.last_name,
          this.account_details.email,
          this.account_details.phone_number,
        ];

      // Filter questions to only mandatory ones
      const mandatory_questions = (this.profile_questions || []).filter(q => q.is_mandatory || q.is_required);
      const mandatory_question_values = mandatory_questions.map((q) => q.editable_value);

      // Filter work experience blocks to only mandatory ones
      const mandatory_we_values = (this.work_experience_blocks || []).flatMap((b) =>
        (b.fields || []).filter(f => f.is_mandatory || f.is_required).map((f) => f.editable_value)
      );

      const all_mandatory = [...mandatory_identity_fields, ...mandatory_question_values, ...mandatory_we_values];
      if (!all_mandatory.length) return 0;

      const filled = all_mandatory.filter((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== null && v !== undefined && String(v).trim() !== '' && v !== 'Not provided';
      }).length;

      return Math.round((filled / all_mandatory.length) * 100);
    },
  },
  watch: {
    entity_form_main_section_ids: {
      handler(new_ids) {
        if (!this.is_entity_form_mode) return;
        if (!Array.isArray(new_ids) || new_ids.length === 0) return;
        if (this.profile_sections.length > 0) return;
        this.fetch_profile_info();
      },
    },

    profile_flow_sections: {
      immediate: true,
      handler(sections) {
        const total = Array.isArray(sections) ? sections.length : 0;
        if (!total) {
          this.current_section_index = 0;
          return;
        }

        if (this.current_section_index > total - 1) {
          this.current_section_index = total - 1;
        }
      },
    },

    profile_completion_percent(value) {
      if (this.is_loading) return;
      this.emit_profile_completion_settled(value);
    },

    is_loading(value) {
      if (value) return;
      this.emit_profile_completion_settled(this.profile_completion_percent);
    },
  },
  mounted() {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) {
      this.$router.replace({ name: 'UserLogin' });
      return;
    }
    this.headers = get_headers();
    this.fetch_profile_info();
  },
  beforeUnmount() {
    if (this.profile_completion_emit_timer) {
      clearTimeout(this.profile_completion_emit_timer);
      this.profile_completion_emit_timer = null;
    }
  },
  methods: {
    get_question_raw_answer(question) {
      const latest_answer = question?.latest_answer || {};
      return latest_answer.answer
        ?? latest_answer.answer_text
        ?? latest_answer.value
        ?? question?.answer
        ?? '';
    },

    get_question_raw_type(question) {
      if (question?.question_type && typeof question.question_type === 'object') {
        return String(question.question_type.question_type || 'short_text').toLowerCase();
      }
      return String(question?.question_type || 'short_text').toLowerCase();
    },

    get_allowed_account_detail_key(identity_key) {
      const allowed_keys = ['first_name', 'last_name', 'email', 'phone_number'];
      return allowed_keys.includes(identity_key) ? identity_key : null;
    },

    get_account_detail_value(identity_key) {
      const safe_key = this.get_allowed_account_detail_key(identity_key);
      return safe_key ? (Reflect.get(this.account_details, safe_key) || '') : '';
    },

    set_account_detail_value(identity_key, value) {
      const safe_key = this.get_allowed_account_detail_key(identity_key);
      if (!safe_key) return;
      Reflect.set(this.account_details, safe_key, value);
    },

    normalize_multi_select_choice(option) {
      return {
        choice: String(option?.choice ?? option?.text ?? option?.label ?? option?.value ?? ''),
      };
    },

    normalize_entity_question(question) {
      return {
        section_question_id: question.id || question.question_id,
        question_text: question.question_text || question.question_name || '',
        question_type: { question_type: question.question_type || 'short_text' },
        answer: this.get_question_raw_answer(question),
        multi_select_choices: (question.options || []).map(this.normalize_multi_select_choice),
      };
    },

    normalize_entity_section(section) {
      return {
        id: section.section_id ?? section.id,
        section_name: section.section_name || section.name || '',
        is_work_experience: Boolean(section.is_work_experience),
        questions: (section.questions || []).map(this.normalize_entity_question),
      };
    },

    has_meaningful_value(value) {
      if (Array.isArray(value)) return value.length > 0;
      if (value === null || value === undefined) return false;
      const text = String(value).trim();
      return text !== '' && text.toLowerCase() !== 'not provided';
    },

    section_has_profile_data(section) {
      if (!section) return false;

      if (section.kind === 'account') {
        return (
          this.has_meaningful_value(this.account_details.first_name)
          || this.has_meaningful_value(this.account_details.last_name)
          || this.has_meaningful_value(this.account_details.email)
          || this.has_meaningful_value(this.account_details.phone_number)
        );
      }

      if (section.kind === 'section') {
        return (section.questions || []).some((question) => this.has_meaningful_value(question.editable_value));
      }

      if (section.kind === 'work') {
        return (this.work_experience_blocks || []).some((block) =>
          (block.fields || []).some((field) => this.has_meaningful_value(field.editable_value)));
      }

      return false;
    },

    get_we_field_type(field_label) {
      const lower = String(field_label || '').toLowerCase();
      if (lower.includes('month')) return 'we_month';
      if (lower.includes('year')) return 'we_year';
      if (lower.includes('reason')) return 'textarea';
      return 'text';
    },

    infer_currently_working_block(block) {
      const fields = block?.fields || [];
      if (!fields.length) return false;

      const is_meaningful_we_value = (value, field_label) => {
        const normalized_value = String(value ?? '').trim();
        if (!normalized_value) return false;

        const lower_value = normalized_value.toLowerCase();
        const lower_label = String(field_label || '').toLowerCase();

        if (lower_value === 'not provided') return false;

        // Ignore UI placeholders that can appear as raw values.
        if (lower_label.includes('month') && lower_value === 'month') return false;
        if (lower_label.includes('year') && lower_value === 'year') return false;
        if (lower_label.includes('reason for leaving') && lower_value === 'reason for leaving') return false;

        return true;
      };

      let has_to_date_value = false;
      let has_non_to_value = false;

      fields.forEach((field) => {
        if (!is_meaningful_we_value(field?.editable_value, field?.label)) return;

        const lower_label = String(field?.label || '').toLowerCase();
        if (lower_label.startsWith('to ')) {
          has_to_date_value = true;
          return;
        }

        has_non_to_value = true;
      });

      return !has_to_date_value && has_non_to_value;
    },

    is_currently_working_block(block) {
      if (typeof block?.currently_work_here === 'boolean') {
        return block.currently_work_here;
      }

      return this.infer_currently_working_block(block);
    },

    should_show_we_field(field, block) {
      const lower_label = String(field?.label || '').toLowerCase();
      if (!this.is_currently_working_block(block)) return true;
      if (lower_label.startsWith('to ')) return false;
      if (lower_label.includes('reason for leaving')) return false;
      return true;
    },

    normalize_profile_question_type(raw_type) {
      const normalized = String(raw_type || 'short_text').toLowerCase();
      if (['phone', 'telephone', 'sa_phone_number'].includes(normalized)) return 'phone';
      if (['file', 'file_upload', 'pdf_file', 'facial_verification'].includes(normalized)) return 'file';
      return normalized;
    },

    does_profile_value_match_trigger(value, triggerCondition, field) {
      if (!triggerCondition) return false;

      if (Array.isArray(value)) {
        const selected_values = value.map((item) => String(item).trim().toLowerCase());
        const trigger = String(triggerCondition).trim().toLowerCase();
        return selected_values.includes(trigger) || selected_values.some((item) => item === trigger);
      }

      let value_to_compare = value;
      if (field?.options?.length && value !== null && value !== undefined) {
        const option = field.options.find((opt) =>
          String(opt.value).trim().toLowerCase() === String(value).trim().toLowerCase(),
        );
        if (option) {
          value_to_compare = option.text;
        }
      }

      const normalized_value = String(value_to_compare || '').trim().toLowerCase();
      const normalized_trigger = String(triggerCondition || '').trim().toLowerCase();
      return normalized_value === normalized_trigger;
    },

    flatten_profile_dependents(nodes, parentPath = []) {
      const out = [];
      if (!Array.isArray(nodes)) return out;

      nodes.forEach((node, idx) => {
        const path = [...parentPath, idx + 1];
        out.push({
          node,
          depth: parentPath.length + 1,
          path,
          key: `${node.model || node.id || 'dq'}-${path.join('-')}`,
        });

        if (Array.isArray(node.dependent_questions) && node.dependent_questions.length) {
          out.push(...this.flatten_profile_dependents(node.dependent_questions, path));
        }
      });

      return out;
    },

    get_profile_dependent_label(node, fallback_index = null) {
      const raw_label = String(
        node?.label
          || node?.question_text
          || node?.question_name
          || node?.question_label
          || node?.title
          || node?.field_label
          || '',
      ).trim();

      if (raw_label && !/^dependent question\s*\d*$/i.test(raw_label)) {
        return raw_label;
      }

      const type = this.normalize_profile_question_type(node?.question_type);
      if (type === 'email') return 'Email';
      if (type === 'phone') return 'Telephone';
      if (type === 'date') return 'Date';
      if (type === 'number') return 'Number';

      const trigger = String(node?.trigger_condition || node?.trigger_value || '').trim();
      if (trigger) {
        return trigger;
      }

      if (fallback_index !== null && fallback_index !== undefined) {
        return `Question ${fallback_index}`;
      }

      return 'Question';
    },

    get_profile_dependent_display_label(node, fallback_index = null) {
      const label = this.get_profile_dependent_label(node, fallback_index);
      return label;
    },

    update_profile_dependent_questions_visibility(field, current_value) {
      if (!Array.isArray(field.dependent_questions) || !field.dependent_questions.length) return;

      const reset_tree = (node) => {
        node.is_visible = false;
        if (Array.isArray(node.dependent_questions) && node.dependent_questions.length) {
          node.dependent_questions.forEach((child) => reset_tree(child));
        }
      };

      const update_tree = (parent_field, parent_value, nodes) => {
        if (!Array.isArray(nodes) || !nodes.length) return;

        nodes.forEach((dependent_question) => {
          const should_be_visible = this.does_profile_value_match_trigger(
            parent_value,
            dependent_question.trigger_condition,
            parent_field,
          );

          dependent_question.is_visible = should_be_visible;

          if (!should_be_visible) {
            reset_tree(dependent_question);
            return;
          }

          const dependent_question_current_value = dependent_question.editable_value;
          if (Array.isArray(dependent_question.dependent_questions) && dependent_question.dependent_questions.length) {
            update_tree(dependent_question, dependent_question_current_value, dependent_question.dependent_questions);
          }
        });
      };

      update_tree(field, current_value, field.dependent_questions);
    },

    restore_profile_dependent_questions_visibility() {
      (this.profile_sections || []).forEach((section) => {
        (section.questions || []).forEach((question) => {
          if (!Array.isArray(question.dependent_questions) || !question.dependent_questions.length) return;
          this.update_profile_dependent_questions_visibility(question, question.editable_value);
        });
      });
    },

    is_phone_question_type(question_type) {
      return this.normalize_profile_question_type(question_type) === 'phone';
    },

    is_file_question_type(question_or_type) {
      const raw_type = question_or_type && typeof question_or_type === 'object'
        ? (question_or_type.original_question_type || question_or_type.question_type)
        : question_or_type;
      return this.normalize_profile_question_type(raw_type) === 'file';
    },

    is_pdf_file_question(question_or_type) {
      const raw_type = question_or_type && typeof question_or_type === 'object'
        ? (question_or_type.original_question_type || question_or_type.question_type)
        : question_or_type;
      return String(raw_type || '').toLowerCase() === 'pdf_file';
    },

    set_profile_validation_error(key, message) {
      if (!key) return;
      if (message) {
        Reflect.set(this.profile_validation_errors, key, message);
      } else {
        Reflect.deleteProperty(this.profile_validation_errors, key);
      }
    },

    parse_single_field_phone(raw_value, fallback_code = '+27') {
      return parse_single_field_phone_input(raw_value, fallback_code);
    },

    on_profile_phone_input(item, event) {
      const parsed = this.parse_single_field_phone(event?.target?.value || item?.editable_value || '', item?._phone_country_code || '+27');
      item._phone_country_code = parsed.country_code;
      item.editable_value = parsed.formatted;
      if (event?.target) {
        event.target.value = parsed.formatted;
      }
      if (parsed.digits.length < 3) {
        this.set_profile_validation_error(item.id, null);
      }
    },

    on_profile_phone_blur(item, required = false, event = null) {
      const parsed = this.parse_single_field_phone(event?.target?.value || item?.editable_value || '', item?._phone_country_code || '+27');
      item._phone_country_code = parsed.country_code;
      item.editable_value = parsed.formatted;
      if (event?.target) {
        event.target.value = parsed.formatted;
      }

      if (!parsed.digits) {
        this.set_profile_validation_error(item.id, required ? 'Phone number is required' : null);
        return;
      }

      const result = validate_phone(parsed.digits, parsed.country_code);
      if (!result.valid) {
        this.set_profile_validation_error(item.id, result.error);
      } else {
        item.editable_value = result.formatted;
        this.set_profile_validation_error(item.id, null);
      }
    },

    build_profile_question(question, section_name, index, question_index, editable_value, identity_key, q_type, raw_type = '') {
      return {
        id: question.section_question_id || question.id || `${index + 1}-${question_index + 1}`,
        label: question.question_text || question.question_name || `Question ${question_index + 1}`,
        section_name,
        question_type: q_type,
        original_question_type: String(raw_type || q_type || '').toLowerCase(),
        options: (question.multi_select_choices || []).map((item) => ({ value: item.choice, text: item.choice })),
        identity_key,
        editable_value,
        is_mandatory: question.is_mandatory || question.required || false,
        is_required: question.is_required || question.required || false,
        is_visible: true,
        dependent_questions: [],
      };
    },

    emit_profile_completion_settled(value) {
      const parsed = Number(value);
      const normalized = Number.isFinite(parsed)
        ? Math.max(0, Math.min(100, Math.round(parsed)))
        : 0;

      if (this.profile_completion_emit_timer) {
        clearTimeout(this.profile_completion_emit_timer);
      }

      // Emit once after profile values settle to avoid intermediate percentages.
      this.profile_completion_emit_timer = setTimeout(() => {
        this.$emit('profile-completion', normalized);
        this.profile_completion_emit_timer = null;
      }, 350);
    },

    goToSection(index) {
      if (index < 0 || index >= this.profile_flow_sections.length) return;
      this.current_section_index = index;
    },

    toggleSection(index) {
      if (this.current_section_index === index) {
        this.current_section_index = -1;
      } else {
        this.current_section_index = index;
      }
      this.is_editing = false;
    },

    getSectionAnsweredLabel(section) {
      if (section.kind === 'account') {
        const fields = [
          this.account_details.first_name,
          this.account_details.last_name,
          this.account_details.email,
          this.account_details.phone_number,
        ];
        const filled = fields.filter((v) => v && String(v).trim()).length;
        return `${filled} of ${fields.length} answered`;
      }
      if (section.kind === 'section') {
        const questions = section.questions || [];
        const total = questions.length;
        const filled = questions.filter((q) => {
          const v = q.editable_value;
          if (Array.isArray(v)) return v.length > 0;
          return v && String(v).trim() && v !== 'Not provided';
        }).length;
        return `${filled} of ${total} answered`;
      }
      if (section.kind === 'work') {
        const blocks = this.work_experience_blocks || [];
        const filled = blocks.filter((b) =>
          b.fields.some((f) => f.editable_value && String(f.editable_value).trim()),
        ).length;
        return `${filled} of ${blocks.length} entries`;
      }
      if (['language', 'education', 'reference'].includes(section.kind)) {
        const blocks = section.blocks || [];
        const filled = blocks.filter((b) =>
          (b.fields || []).some((f) => {
            const value = f.allows_other && String(f.editable_value || '').toLowerCase() === 'other'
              ? f.other_value
              : f.editable_value;
            return value && String(value).trim() && value !== 'Not provided';
          }),
        ).length;
        return `${filled} of ${blocks.length} entries`;
      }
      return '';
    },

    goToPrevSection() {
      if (this.current_section_index <= 0) return;
      this.current_section_index -= 1;
    },

    goToNextSection() {
      if (this.current_section_index >= this.profile_flow_sections.length - 1) return;
      this.current_section_index += 1;
    },

    format_date(date_value) {
      if (!date_value) return 'Not provided';
      const parsed = new Date(date_value);
      if (Number.isNaN(parsed.getTime())) return String(date_value);
      return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },

    should_use_identity_default(question_label) {
      const label = String(question_label || '').toLowerCase();
      const first_name_match = /(first\s*name|name\s*of\s*applicant|given\s*name)/.test(label);
      const last_name_match = /(last\s*name|surname|family\s*name)/.test(label);
      const email_match = /(email|e-mail)/.test(label);
      const phone_match = /(phone|mobile|contact\s*number|telephone|cell\s*number)/.test(label);
      return first_name_match || last_name_match || email_match || phone_match;
    },

    get_identity_key(question_label) {
      const label = String(question_label || '').toLowerCase();
      if (/(first\s*name|name\s*of\s*applicant|given\s*name)/.test(label)) return 'first_name';
      if (/(last\s*name|surname|family\s*name)/.test(label)) return 'last_name';
      if (/(email|e-mail)/.test(label)) return 'email';
      if (/(phone|mobile|contact\s*number|telephone|cell\s*number)/.test(label)) return 'phone_number';
      if (/^name$/.test(label.trim())) return 'first_name';
      return null;
    },

    get_identity_default_value(question_label) {
      const label = String(question_label || '').toLowerCase();
      if (/(first\s*name|name\s*of\s*applicant|given\s*name)/.test(label)) {
        return this.identity_defaults.first_name || '';
      }
      if (/(last\s*name|surname|family\s*name)/.test(label)) {
        return this.identity_defaults.last_name || '';
      }
      if (/(email|e-mail)/.test(label)) {
        return this.identity_defaults.email || '';
      }
      if (/(phone|mobile|contact\s*number|telephone|cell\s*number)/.test(label)) {
        return this.identity_defaults.phone_number || '';
      }
      return '';
    },

    _get_profile_section_kind(section) {
      const name = String(section?.section_name || section?.name || '').trim().toLowerCase();
      const question_labels = (section?.questions || []).map((question) =>
        String(question?.question_text || question?.question_name || '').trim(),
      );

      const has_language_questions = question_labels.some((label) =>
        /^\[LANG\s+\d+\]/i.test(label)
          || /^Language\s+\d+\.?$/i.test(label)
          || /^Level\s+of\s+speaking\s*\(Language\s*\d+\)/i.test(label)
          || /^Level\s+of\s+Reading\s+or\s+Writing\s*\(Language\s*\d+\)/i.test(label),
      );
      const has_education_questions = question_labels.some((label) =>
        /^\[EDU\s+\d+\]/i.test(label)
          || /^Name\s+of\s+School\/Technical\s+College\s*\d+/i.test(label)
          || /^Name\s+of\s+qualification\s*(\d+)?\s*obtained/i.test(label)
          || /^Year\s+qualification\s*\d+\s+was\s+obtained/i.test(label)
          || /^Start\s+(date|year)/i.test(label)
          || /^Expected\s+(completion|date|year)/i.test(label)
          || /NQF\s*Level/i.test(label),
      );
      const has_reference_questions = question_labels.some((label) =>
        /^\[REF\s+\d+\]/i.test(label)
          || /^Name\s*\(Reference\s*\d+\)/i.test(label)
          || /^Relationship\s+to\s+you\s*\(Reference\s*\d+\)/i.test(label)
          || /^Tel\.?\s*No\.?\s*\(office\s+hours\)\s*\(Reference\s*\d+\)/i.test(label),
      );

      if (section?.is_language || has_language_questions || name === 'languages' || name === 'language' || name.includes('language proficiency') || name === 'south african official language proficiency') {
        return 'language';
      }
      if (section?.is_education || has_education_questions || name === 'education' || name.includes('qualification') || name === 'formal qualification (from highest to the lowest)') {
        return 'education';
      }
      if (section?.is_reference || has_reference_questions || name === 'references' || name === 'reference') {
        return 'reference';
      }
      return 'section';
    },

    _get_official_language_options() {
      return get_official_language_options();
    },

    _get_language_proficiency_options() {
      return get_language_proficiency_options();
    },

    _get_reference_relationship_options() {
      return ['Supervisor', 'Manager', 'Lecturer', 'Colleague', 'Mentor', 'Client', 'Community Leader', 'Other']
        .map((item) => ({ text: item, value: item }));
    },

    _get_profile_year_options(start = 1960) {
      const current_year = new Date().getFullYear();
      const years = [];
      for (let y = current_year; y >= start; y -= 1) {
        years.push({ text: String(y), value: String(y) });
      }
      return years;
    },

    _map_other_option_value(rawValue, options = []) {
      const normalized = rawValue === 'Not provided' ? '' : String(rawValue || '').trim();
      if (!normalized) {
        return { editable_value: '', other_value: '' };
      }

      const matched = options.find((option) => String(option.value).toLowerCase() === normalized.toLowerCase());
      if (matched) {
        return { editable_value: matched.value, other_value: '' };
      }

      return { editable_value: 'Other', other_value: normalized };
    },

    _is_numeric_question_id(value) {
      if (typeof value === 'number') {
        return Number.isInteger(value);
      }

      if (typeof value === 'string') {
        return /^\d+$/.test(value.trim());
      }

      return false;
    },

    _structured_section_has_value(field) {
      const value = field.allows_other && String(field.editable_value || '').toLowerCase() === 'other'
        ? field.other_value
        : field.editable_value;

      if (Array.isArray(value)) return value.length > 0;
      if (value === null || value === undefined) return false;

      return String(value).trim() !== '';
    },

    _strip_structured_prefix(label, prefix) {
      return String(label || '').replace(new RegExp(`^\\[${prefix}\\s+\\d+\\]\\s*`, 'i'), '').trim();
    },

    _create_answer_payload(question_id, answer_text) {
      if (!this._is_numeric_question_id(question_id)) {
        return null;
      }

      return {
        section_question_id: Number(question_id),
        answer_text,
      };
    },

    _build_default_profile_card_section(section, kind) {
      const section_name = section.section_name || section.name || '';
      const options_years = this._get_profile_year_options();
      const fields_by_kind = {
        language: [
          {
            id: `${section_name}-lang-1`,
            label: 'Language 1',
            original_label: 'Language 1',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'single_select',
            options: this._get_official_language_options(),
            allows_other: true,
          },
          {
            id: `${section_name}-lang-2`,
            label: 'Level of Speaking',
            original_label: 'Level of Speaking',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'single_select',
            options: this._get_language_proficiency_options(),
            allows_other: false,
          },
          {
            id: `${section_name}-lang-3`,
            label: 'Level of Reading/Writing',
            original_label: 'Level of Reading/Writing',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'single_select',
            options: this._get_language_proficiency_options(),
            allows_other: false,
          },
        ],
        education: [
          {
            id: `${section_name}-edu-1`,
            label: 'Institution',
            original_label: 'Institution',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'text',
            options: [],
            allows_other: false,
          },
          {
            id: `${section_name}-edu-2`,
            label: 'Qualification',
            original_label: 'Qualification',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'text',
            options: [],
            allows_other: false,
          },
          {
            id: `${section_name}-edu-3`,
            label: 'Year Obtained',
            original_label: 'Year Obtained',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'single_select',
            options: options_years,
            allows_other: false,
          },
        ],
        reference: [
          {
            id: `${section_name}-ref-1`,
            label: 'Name',
            original_label: 'Name',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'text',
            options: [],
            allows_other: false,
          },
          {
            id: `${section_name}-ref-2`,
            label: 'Relationship',
            original_label: 'Relationship',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'single_select',
            options: this._get_reference_relationship_options(),
            allows_other: true,
          },
          {
            id: `${section_name}-ref-3`,
            label: 'Tel. No. (office hours)',
            original_label: 'Tel. No. (office hours)',
            editable_value: '',
            other_value: '',
            is_mandatory: false,
            is_required: false,
            question_type: 'phone',
            options: [],
            allows_other: false,
          },
        ],
      };

      const fields = fields_by_kind[kind] || [];
      const block = {
        index: 0,
        label: `${kind === 'language' ? 'Language' : kind === 'education' ? 'Qualification' : 'Reference'} 1`,
        fields,
      };

      return {
        id: section.id || section.section_id || section_name,
        name: section_name,
        kind,
        description: kind === 'language'
          ? 'Review your language proficiency details.'
          : kind === 'education'
            ? 'Review your qualification details.'
            : 'Review your references.',
        questions: fields,
        blocks: [block],
        all_blocks: [block],
      };
    },

    _build_profile_card_section(section, kind) {
      const blockMap = {};
      const section_name = section.section_name || section.name || '';
      const pushField = (block_num, field) => {
        if (!blockMap[block_num]) blockMap[block_num] = [];
        blockMap[block_num].push(field);
      };

      (section.questions || []).forEach((question, question_index) => {
        const label = String(question.question_text || question.question_name || `Question ${question_index + 1}`).trim();
        const normalized_label = label.replace(/\s+/g, ' ').trim();
        const raw_value = normalize_answer_value(question.answer);
        const base_field = {
          id: question.section_question_id || question.id || `${section.id || section_name}-${question_index + 1}`,
          original_label: label,
          editable_value: raw_value === 'Not provided' ? '' : raw_value,
          is_mandatory: question.is_mandatory || question.required || false,
          is_required: question.is_required || question.required || false,
          question_type: 'text',
          options: [],
          allows_other: false,
          other_value: '',
        };

        if (kind === 'language') {
          const prefix_match = normalized_label.match(/^\[LANG\s+(\d+)\]\s*(.*)$/i);
          const stripped_label = this._strip_structured_prefix(normalized_label, 'LANG');
          const language_match = stripped_label.match(/^Language\s+(\d+)\.?$/i);
          const speak_match = stripped_label.match(/^Level\s+of\s+speaking\s*\(Language\s*(\d+)\)$/i);
          const rw_match = stripped_label.match(/^Level\s+of\s+Reading\s+or\s+Writing\s*\(Language\s*(\d+)\)$/i);
          const prefixed_block_num = prefix_match ? parseInt(prefix_match[1], 10) : null;

          if (language_match) {
            const options = this._get_official_language_options();
            const mapped = map_language_selection_value(base_field.editable_value);
            pushField(prefixed_block_num || parseInt(language_match[1], 10), {
              ...base_field,
              label: `Language ${prefixed_block_num || language_match[1]}`,
              question_type: 'single_select',
              options,
              editable_value: mapped.editable_value,
              other_value: mapped.other_value,
              allows_other: true,
            });
          } else if (speak_match) {
            pushField(prefixed_block_num || parseInt(speak_match[1], 10), {
              ...base_field,
              label: 'Level of Speaking',
              question_type: 'single_select',
              options: this._get_language_proficiency_options(),
              editable_value: normalize_language_proficiency_value(base_field.editable_value),
            });
          } else if (rw_match) {
            pushField(prefixed_block_num || parseInt(rw_match[1], 10), {
              ...base_field,
              label: 'Level of Reading/Writing',
              question_type: 'single_select',
              options: this._get_language_proficiency_options(),
              editable_value: normalize_language_proficiency_value(base_field.editable_value),
            });
          }
        }

        if (kind === 'education') {
          const prefix_match = normalized_label.match(/^\[EDU\s+(\d+)\]\s*(.*)$/i);
          const stripped_label = this._strip_structured_prefix(normalized_label, 'EDU');
          const school_match = stripped_label.match(/^Name\s+of\s+School\/Technical\s+College\s*(\d+)$/i);
          const qualification_match = stripped_label.match(/^Name\s+of\s+qualification\s*(\d+)?\s*obtained$/i);
          const year_match = stripped_label.match(/^Year\s+qualification\s*(\d+)\s+was\s+obtained$/i);
          const prefixed_block_num = prefix_match ? parseInt(prefix_match[1], 10) : null;

          if (school_match) {
            pushField(prefixed_block_num || parseInt(school_match[1], 10), {
              ...base_field,
              label: 'Institution',
            });
          } else if (qualification_match) {
            const block_num = prefixed_block_num || parseInt(qualification_match[1], 10) || 1;
            pushField(block_num, {
              ...base_field,
              label: 'Qualification',
            });
          } else if (year_match) {
            pushField(prefixed_block_num || parseInt(year_match[1], 10), {
              ...base_field,
              label: 'Year Obtained',
              question_type: 'single_select',
              options: this._get_profile_year_options(),
            });
          }
        }

        if (kind === 'reference') {
          const prefix_match = normalized_label.match(/^\[REF\s+(\d+)\]\s*(.*)$/i);
          const stripped_label = this._strip_structured_prefix(normalized_label, 'REF');
          const name_match = stripped_label.match(/^Name\s*\(Reference\s*(\d+)\)$/i);
          const relation_match = stripped_label.match(/^Relationship\s+to\s+you\s*\(Reference\s*(\d+)\)$/i);
          const phone_match = stripped_label.match(/^Tel\.?\s*No\.?\s*\(office\s+hours\)\s*\(Reference\s*(\d+)\)$/i);
          const prefixed_block_num = prefix_match ? parseInt(prefix_match[1], 10) : null;

          if (name_match) {
            pushField(prefixed_block_num || parseInt(name_match[1], 10), {
              ...base_field,
              label: 'Name',
            });
          } else if (relation_match) {
            const options = this._get_reference_relationship_options();
            const mapped = this._map_other_option_value(base_field.editable_value, options);
            pushField(prefixed_block_num || parseInt(relation_match[1], 10), {
              ...base_field,
              label: 'Relationship',
              question_type: 'single_select',
              options,
              editable_value: mapped.editable_value,
              other_value: mapped.other_value,
              allows_other: true,
            });
          } else if (phone_match) {
            pushField(prefixed_block_num || parseInt(phone_match[1], 10), {
              ...base_field,
              label: 'Tel. No. (office hours)',
              question_type: 'phone',
            });
          }
        }
      });

      const sortedBlockNums = Object.keys(blockMap).map(Number).sort((a, b) => a - b);
      const all_blocks = sortedBlockNums.map((num, idx) => ({
        index: idx,
        label: `${kind === 'language' ? 'Language' : kind === 'education' ? 'Qualification' : 'Reference'} ${idx + 1}`,
        fields: blockMap[num],
      }));

      if (!all_blocks.length) return this._build_default_profile_card_section(section, kind);

      const has_data = (block) => (block.fields || []).some((field) => {
        const value = field.allows_other && String(field.editable_value || '').toLowerCase() === 'other'
          ? field.other_value
          : field.editable_value;
        return value && String(value).trim() !== '';
      });

      const visible_blocks = all_blocks.filter(has_data);
      const blocks = visible_blocks.length ? visible_blocks : [all_blocks[0]];

      return {
        id: section.id || section.section_id || section_name,
        name: section_name,
        kind,
        description: kind === 'language'
          ? 'Review your language proficiency details.'
          : kind === 'education'
            ? 'Review your qualification details.'
            : 'Review your references.',
        questions: all_blocks.flatMap((block) => block.fields),
        blocks,
        all_blocks,
      };
    },

    get_structured_icon(kind) {
      if (kind === 'language') return 'bi-translate';
      if (kind === 'education') return 'bi-mortarboard';
      if (kind === 'reference') return 'bi-people';
      return 'bi-grid';
    },

    get_structured_add_label(kind) {
      if (kind === 'language') return 'Add Language';
      if (kind === 'education') return 'Add Qualification';
      if (kind === 'reference') return 'Add Reference';
      return 'Add';
    },

    add_structured_block(section) {
      if (!section?.blocks || !section?.all_blocks) return;
      if (section.blocks.length >= section.all_blocks.length) return;
      section.blocks.push(section.all_blocks[section.blocks.length]);
    },

    remove_structured_block(section, blockIndex) {
      if (!section?.blocks || section.blocks.length <= 1) return;
      const removed = section.blocks[blockIndex];
      if (removed) {
        (removed.fields || []).forEach((field) => {
          field.editable_value = '';
          field.other_value = '';
        });
      }
      section.blocks.splice(blockIndex, 1);
    },

    async fetch_user_details() {
      const response = await axios.get(GET_USER_PROFILE_DETAILS_URL, {
        headers: this.headers,
      });

      const user_data = response?.data?.data || {};
      const first_name = user_data.first_name || '';
      const last_name = user_data.last_name || '';
      const full_name = user_data.full_name || `${first_name} ${last_name}`.trim() || 'User';

      this.account_details = {
        full_name,
        first_name,
        last_name,
        email: user_data.email || '',
        phone_number: user_data.phone_number || '',
        date_joined: this.format_date(user_data.date_joined),
      };

      this.identity_defaults = {
        first_name,
        last_name,
        email: user_data.email || '',
        phone_number: user_data.phone_number || '',
      };
    },

    get_profile_cache_key() {
      const user_id = sessionStorage.getItem('user_id') || 'anonymous';
      const scope = this.is_entity_form_mode
        ? [
          'entity',
          this.application_id || sessionStorage.getItem('application_id') || '',
          this.entity_profile_id || sessionStorage.getItem('entity_profile_id') || '',
          this.entity_application_form_id || sessionStorage.getItem('application_form_id') || '',
        ].join(':')
        : 'profile';

      return `profile-info-cache:${user_id}:${scope}`;
    },

    hydrate_profile_from_cache() {
      const raw = sessionStorage.getItem(this.get_profile_cache_key());
      if (!raw) return false;

      const cached = parse_json_or_null(raw);
      if (!cached || typeof cached !== 'object') return false;

      this.account_details = cached.account_details || this.account_details;
      this.identity_defaults = cached.identity_defaults || this.identity_defaults;
      this.has_profile_form = cached.has_profile_form !== false;
      this.profile_form_id = cached.profile_form_id || null;
      this.profile_title = cached.profile_title || this.profile_title;
      this.hidden_synced_questions = cached.hidden_synced_questions || [];
      this.profile_sections = cached.profile_sections || [];
      this.profile_questions = cached.profile_questions || [];
      this.work_experience_blocks = cached.work_experience_blocks || [];
      this.current_section_index = Number.isFinite(cached.current_section_index)
        ? cached.current_section_index
        : this.current_section_index;

      return true;
    },

    persist_profile_cache() {
      const payload = {
        account_details: this.account_details,
        identity_defaults: this.identity_defaults,
        has_profile_form: this.has_profile_form,
        profile_form_id: this.profile_form_id,
        profile_title: this.profile_title,
        hidden_synced_questions: this.hidden_synced_questions,
        profile_sections: this.profile_sections,
        profile_questions: this.profile_questions,
        work_experience_blocks: this.work_experience_blocks,
        current_section_index: this.current_section_index,
      };
      sessionStorage.setItem(this.get_profile_cache_key(), JSON.stringify(payload));
    },

    clear_profile_cache() {
      sessionStorage.removeItem(this.get_profile_cache_key());
    },

    async fetch_profile_info() {
      const has_cached_profile = this.hydrate_profile_from_cache();
      this.is_loading = !has_cached_profile;
      this.has_profile_form = true;

      try {
        const forms = await this.get_profile_forms();

        if (!forms.length) {
          this.reset_profile_state();
          return;
        }

        await this.process_profile_forms(forms);
      } catch (error) {
        this.handle_profile_fetch_error(error, has_cached_profile);
      } finally {
        this.is_loading = false;
      }
    },

    async get_profile_forms() {
      if (this.is_entity_form_mode) {
        return await this.get_entity_profile_forms();
      }

      return await this.get_standard_profile_forms();
    },

    async get_entity_profile_forms() {
      const application_id = Number(this.application_id || sessionStorage.getItem('application_id')) || null;
      const entity_profile_id = Number(this.entity_profile_id || sessionStorage.getItem('entity_profile_id')) || null;
      const application_form_id = Number(this.entity_application_form_id || sessionStorage.getItem('application_form_id')) || null;

      const main_section_ids = (this.entity_form_main_section_ids || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));

      const tab_requests = main_section_ids.map((form_main_section_id) =>
        axios.get(GET_TABS_DETAILS, {
          headers: this.headers,
          params: {
            form_main_section_id,
            application_form_id,
            application_id,
            entity_profile_id,
            current_status_id: null,
          },
        }));

      const tab_responses = await Promise.all(tab_requests);

      const raw_sections = tab_responses.flatMap((res) => {
        const main_rows = Array.isArray(res?.data?.data) ? res.data.data : [];

        return main_rows.flatMap((main) =>
          (main.sections || []).map((sec) => ({
            id: sec.section_id ?? sec.id,
            section_name: sec.section_name || sec.name || '',
            is_work_experience: Boolean(sec.is_work_experience),
            is_language: Boolean(sec.is_language),
            is_education: Boolean(sec.is_education),
            is_reference: Boolean(sec.is_reference),
            questions: (sec.questions || []).map((q) => ({
              section_question_id: q.id || q.question_id,
              question_text: q.question_text || q.question_name || '',
              question_type: { question_type: q.question_type || 'short_text' },
              answer: q?.latest_answer?.answer ?? '',
              multi_select_choices: (q.options || []).map((option) => ({
                choice: String(option.choice ?? option.text ?? option.label ?? option.value ?? ''),
              })),
            })),
          })));
      });

      return [{
        application_form_id,
        form_name: 'Entity Information',
        form: { sections: raw_sections },
      }];
    },

    async get_standard_profile_forms() {
      const [, response] = await Promise.all([
        this.fetch_user_details(),
        axios.get(GET_APPLICATION_FORMS, {
          headers: this.headers,
          params: { profile: 1 },
        }),
      ]);

      const response_body = response.data;

      return Array.isArray(response_body)
        ? response_body
        : (Array.isArray(response_body?.data)
          ? response_body.data
          : (response_body?.data ? [response_body.data] : []));
    },

    reset_profile_state() {
      this.has_profile_form = false;
      this.profile_form_id = null;
      this.profile_questions = [];
      this.hidden_synced_questions = [];
      this.profile_sections = [];
      this.work_experience_blocks = [];
      this.clear_profile_cache();
    },

    async process_profile_forms(forms) {
      const payload = forms[0] || {};

      this.profile_form_id = payload.application_form_id || payload.id || null;

      const raw_sections = payload?.form?.sections || [];

      this.profile_title = payload.form_name
        || (this.is_entity_form_mode ? 'Entity Information' : 'Profile Information');

      this.hidden_synced_questions = [];

      const parsed_sections = this.parse_profile_sections(raw_sections);

      this.profile_sections = parsed_sections.section_cards;
      this.profile_questions = parsed_sections.section_cards.flatMap((s) => s.questions || []);

      this.setup_work_experience_blocks(parsed_sections.we_map);

      this.restore_profile_dependent_questions_visibility();

      this.set_initial_profile_section();
      this.persist_profile_cache();
    },

    parse_profile_sections(raw_sections) {
      const we_regex = /^\[WE\s+(\d+)\]\s*(.*)/i;
      const we_unnumbered_regex = /^\[WE\]\s*/i;

      const we_map = new Map();
      const identity_shown = new Set();
      const section_cards = [];

      raw_sections.forEach((section, index) => {
        const parsed = this.parse_single_profile_section(
          section,
          index,
          we_regex,
          we_unnumbered_regex,
          we_map,
          identity_shown,
        );

        if (parsed) {
          section_cards.push(parsed);
        }
      });

      return {
        section_cards,
        we_map,
      };
    },

    parse_single_profile_section(
      section,
      index,
      we_regex,
      we_unnumbered_regex,
      we_map,
      identity_shown,
    ) {
      const section_name = section.section_name || section.name || `Section ${index + 1}`;
      const section_kind = this._get_profile_section_kind(section);

      if (['language', 'education', 'reference'].includes(section_kind)) {
        return this._build_profile_card_section(section, section_kind);
      }

      const section_questions = [];

      (section.questions || []).forEach((question, question_index) => {
        const result = this.build_profile_question_object(
          question,
          question_index,
          section,
          section_name,
          index,
          we_regex,
          we_unnumbered_regex,
          we_map,
          identity_shown,
        );

        if (result) {
          section_questions.push(result);
        }
      });

      if (!section_questions.length) {
        return null;
      }

      return {
        id: section.id || `sec-${index + 1}`,
        name: section_name,
        questions: section_questions,
      };
    },

    build_profile_question_object(
      question,
      question_index,
      section,
      section_name,
      index,
      we_regex,
      we_unnumbered_regex,
      we_map,
      identity_shown,
    ) {
      const label = question.question_text
        || question.question_name
        || `Question ${question_index + 1}`;

      const identity_key = this.is_entity_form_mode
        ? null
        : this.get_identity_key(label);

      const normalized = normalize_answer_value(
        this.get_question_raw_answer(question),
      );

      const fallback = this.get_identity_default_value(label);

      const raw_q_type = this.get_question_raw_type(question);

      const q_type = this.normalize_profile_question_type(raw_q_type);

      const editable_val = this.get_profile_question_editable_value(
        question,
        identity_key,
        normalized,
        fallback,
        q_type,
      );

      const q_obj = this.build_profile_question(
        question,
        section_name,
        index,
        question_index,
        editable_val,
        identity_key,
        q_type,
        raw_q_type,
      );

      q_obj.dependent_questions = this.build_dependent_questions(question, q_obj);

      const match = label.match(we_regex);

      if (match || we_unnumbered_regex.test(label) || section.is_work_experience) {
        this.add_work_experience_question(match, q_obj, label, we_map);
        return null;
      }

      if (identity_key) {
        if (identity_shown.has(identity_key)) return null;
        identity_shown.add(identity_key);
      }

      return q_obj;
    },

    get_profile_question_editable_value(
      question,
      identity_key,
      normalized,
      fallback,
      q_type,
    ) {
      if (identity_key) {
        const account_value = this.get_account_detail_value(identity_key);
        const answer_value = normalized === 'Not provided' ? '' : normalized;

        const editable_val =
          account_value || answer_value || fallback || '';

        if (!account_value && answer_value) {
          this.set_account_detail_value(identity_key, answer_value);
        }

        return editable_val;
      }

      if (q_type === 'multi_select') {
        return parse_multi_select_answer(question.answer);
      }

      return normalized === 'Not provided'
        ? (fallback || '')
        : normalized;
    },

    build_dependent_questions(question, q_obj) {
      const raw_dependents = Array.isArray(question.dependant_questions)
        ? question.dependant_questions
        : (Array.isArray(question.dependent_questions)
          ? question.dependent_questions
          : []);

      return raw_dependents.map((dep, dep_index) =>
        this.build_single_dependent_question(dep, dep_index, q_obj));
    },

    get_dependent_identity_key(dep_label) {
      return this.is_entity_form_mode ? null : this.get_identity_key(dep_label);
    },

    get_dependent_options(dep) {
      return (dep.multi_select_choices || dep.options || []).map((item) => ({
        value: String(item.choice ?? item.text ?? item.label ?? item.value ?? ''),
        text: String(item.choice ?? item.text ?? item.label ?? item.value ?? ''),
      }));
    },

    get_dependent_editable_value(dep_label, dep_normalized) {
      const dep_fallback = this.get_identity_default_value(dep_label);
      return dep_normalized === 'Not provided'
        ? (dep_fallback || '')
        : dep_normalized;
    },

    build_single_dependent_question(dep, dep_index, q_obj) {
      const dep_label = this.get_profile_dependent_display_label(
        dep,
        dep_index + 1,
      );

      const dep_identity_key = this.get_dependent_identity_key(dep_label);

      const dep_normalized = normalize_answer_value(
        this.get_question_raw_answer(dep),
      );

      const dep_raw_q_type = this.get_question_raw_type(dep);

      const dep_q_type = this.normalize_profile_question_type(dep_raw_q_type);

      const dep_options = this.get_dependent_options(dep);

      return {
        id: dep.section_question_id || dep.id || `${q_obj.id}-dep-${dep_index + 1}`,
        section_question_id: dep.section_question_id || dep.id || null,
        label: dep_label,
        display_label: dep_label,
        question_type: dep_q_type,
        original_question_type: String(dep_raw_q_type || dep_q_type || '').toLowerCase(),
        options: dep_options,
        identity_key: dep_identity_key,
        editable_value: dep_identity_key
          ? (
            this.get_account_detail_value(dep_identity_key)
            || (dep_normalized === 'Not provided' ? '' : dep_normalized)
            || this.get_identity_default_value(dep_label)
            || ''
          )
          : this.get_dependent_editable_value(dep_label, dep_normalized),
        is_mandatory: dep.is_mandatory || dep.required || false,
        is_required: dep.is_required || dep.required || false,
        is_visible: false,
        trigger_condition: dep.trigger_condition || dep.trigger_value || '',
        dependent_questions: this.build_nested_dependent_questions(
          dep,
          dep_index,
          q_obj,
        ),
      };
    },

    build_question_options(options = []) {
      return options.map((item) => this.build_single_question_option(item));
    },

    build_single_question_option(item) {
      const option_value = String(
        item.choice
        ?? item.text
        ?? item.label
        ?? item.value
        ?? '',
      );

      return {
        value: option_value,
        text: option_value,
      };
    },

    build_nested_dependent_questions(dep, dep_index, q_obj) {
      if (!Array.isArray(dep.dependant_questions)) {
        return [];
      }

      return dep.dependant_questions.map((child, child_index) => ({
        id: child.section_question_id
          || child.id
          || `${q_obj.id}-dep-${dep_index + 1}-${child_index + 1}`,

        section_question_id: child.section_question_id || child.id || null,

        label: this.get_profile_dependent_display_label(
          child,
          child_index + 1,
        ),

        display_label: this.get_profile_dependent_display_label(
          child,
          child_index + 1,
        ),

        question_type: this.normalize_profile_question_type(
          this.get_question_raw_type(child),
        ),

        original_question_type: String(
          this.get_question_raw_type(child) || 'short_text',
        ).toLowerCase(),

        options: this.build_question_options(
        child.multi_select_choices || child.options || []),

        identity_key: this.is_entity_form_mode
          ? null
          : this.get_identity_key(child.question_text || child.question_name || ''),

        editable_value: normalize_answer_value(
          this.get_question_raw_answer(child),
        ),

        is_mandatory: child.is_mandatory || child.required || false,
        is_required: child.is_required || child.required || false,
        is_visible: false,
        trigger_condition: child.trigger_condition || child.trigger_value || '',
        dependent_questions: [],
      }));
    },

    add_work_experience_question(match, q_obj, label, we_map) {
      if (!match) return;

      const block_num = parseInt(match[1], 10);
      const field_label = match[2];
      const field_type = this.get_we_field_type(field_label);

      if (!we_map.has(block_num)) {
        we_map.set(block_num, []);
      }

      we_map.get(block_num).push({
        id: q_obj.id,
        label: field_label,
        field_type,
        editable_value: q_obj.editable_value === 'Not provided'
          ? ''
          : q_obj.editable_value,
        is_mandatory: q_obj.is_mandatory,
        is_required: q_obj.is_required,
      });
    },

    setup_work_experience_blocks(we_map) {
      const block_nums = Array.from(we_map.keys()).sort((a, b) => a - b);

      const all_we_blocks = block_nums.map((num, idx) => ({
        index: idx,
        label: `Experience ${idx + 1}`,
        fields: we_map.get(num) || [],
        currently_work_here: undefined,
      }));

      all_we_blocks.forEach((block) => {
        block.currently_work_here = this.infer_currently_working_block(block);
      });

      this._all_we_blocks = all_we_blocks;

      const _we_has_data = (b) => b.fields.some((f) => {
        const v = f.editable_value;

        if (!v) return false;

        const s = String(v).trim();

        return s !== '' && s !== '0';
      });

      if (all_we_blocks.length > 0) {
        const with_data = all_we_blocks.filter(_we_has_data);

        this.work_experience_blocks =
          with_data.length > 0
            ? with_data
            : [all_we_blocks[0]];
      } else {
        this.work_experience_blocks = [];
      }
    },

    set_initial_profile_section() {
      const has_value = (value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value === null || value === undefined) return false;

        const text = String(value).trim();

        return text !== '' && text.toLowerCase() !== 'not provided';
      };

      const first_filled_index = this.profile_flow_sections.findIndex((section) => {
        if (section.kind === 'account') {
          return (
            has_value(this.account_details.first_name)
            || has_value(this.account_details.last_name)
            || has_value(this.account_details.email)
            || has_value(this.account_details.phone_number)
          );
        }

        if (section.kind === 'section') {
          return (section.questions || []).some((question) =>
            has_value(question.editable_value));
        }

        if (section.kind === 'work') {
          return (this.work_experience_blocks || []).some((block) =>
            (block.fields || []).some((field) =>
              has_value(field.editable_value)));
        }

        if (['language', 'education', 'reference'].includes(section.kind)) {
          return (section.blocks || []).some((block) =>
            (block.fields || []).some((field) => {
              const value = field.allows_other
                && String(field.editable_value || '').toLowerCase() === 'other'
                ? field.other_value
                : field.editable_value;

              return has_value(value);
            }));
        }

        return false;
      });

      this.current_section_index =
        first_filled_index >= 0
          ? first_filled_index
          : 0;
    },

    handle_profile_fetch_error(error, has_cached_profile = false) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        this.$router.replace({ name: 'UserLogin' });
        return;
      }

      if (has_cached_profile) {
        return;
      }

      this.show_error(
        error?.response?.data?.message
        || 'Could not load profile information',
      );

      this.reset_profile_state();
    },

    toggle_password_visibility() {
      this.password_input_type = this.password_input_type === 'password' ? 'text' : 'password';
    },

    toggle_confirm_password_visibility() {
      this.confirm_password_input_type = this.confirm_password_input_type === 'password' ? 'text' : 'password';
    },

    sync_identity_back_to_questions() {
      this.profile_questions = this.profile_questions.map((question) => {
        if (!question.identity_key) return question;
        return {
          ...question,
          editable_value: this.account_details[question.identity_key] || '',
        };
      });
    },

    async save_account_details() {
      const payload = {
        user_id: sessionStorage.getItem('user_id'),
        first_name: this.account_details.first_name,
        last_name: this.account_details.last_name,
        email: this.account_details.email,
        phone_number: this.account_details.phone_number,
      };

      await axios.post(UPDATE_USER_URL, payload, { headers: this.headers });
    },

    async save_profile_answers() {
      if (!this.profile_form_id) return;

      const kind = this.current_profile_section?.kind;

      const section_questions = kind === 'section'
        ? (this.current_profile_section.questions || [])
        : [];

      const visible_answers = section_questions
        .map((question) => this._create_answer_payload(
          question.id,
          Array.isArray(question.editable_value)
            ? question.editable_value.join(', ')
            : (question.editable_value || ''),
        ))
        .filter(Boolean);

      const synced_identity_answers = this.hidden_synced_questions
        .map((question) => this._create_answer_payload(
          question.id,
          this.account_details[question.identity_key] || '',
        ))
        .filter(Boolean);

      const we_answers = kind === 'work'
        ? this.work_experience_blocks.flatMap((block) =>
            block.fields
              .map((f) => this._create_answer_payload(
                f.id,
                (f.editable_value || '').toString().trim(),
              ))
              .filter(Boolean),
          )
        : [];

      const structured_answers = ['language', 'education', 'reference'].includes(kind)
        ? (this.current_profile_section.blocks || []).flatMap((block) =>
            (block.fields || [])
              .map((field) => this._create_answer_payload(
                field.id,
                (
                  field.allows_other && String(field.editable_value || '').toLowerCase() === 'other'
                    ? (field.other_value || '')
                    : (field.editable_value || '')
                ).toString().trim(),
              ))
              .filter(Boolean),
          )
        : [];

      const answers = [...visible_answers, ...synced_identity_answers, ...we_answers, ...structured_answers];

      const form_data = new FormData();
      form_data.append('application_form_id', String(this.profile_form_id));
      form_data.append('current_section_index', '0');
      form_data.append('answers', JSON.stringify(answers));

      section_questions.forEach((question) => {
        if (question.new_file) {
          form_data.append(`file_${question.id}`, question.new_file);
        }
      });

      await axios.post(SUBMIT_APPLICATION_FORMS_ANSWERS, form_data, {
        headers: {
          ...this.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
    },

    async save_password_if_provided() {
      const user_id = sessionStorage.getItem('user_id');
      const wants_password_update = Boolean(this.new_password || this.confirm_password);
      if (!wants_password_update) return;

      if (!this.new_password || !this.confirm_password) {
        throw new Error('Please complete both password fields.');
      }

      if (this.new_password !== this.confirm_password) {
        throw new Error('Passwords do not match.');
      }

      const payload = {
        user_id,
        first_name: this.account_details.first_name,
        last_name: this.account_details.last_name,
        phone_number: this.account_details.phone_number,
        new_password: this.new_password,
        confirm_password: this.confirm_password,
      };

      await axios.post(FIRST_TIME_LOGIN_URL, payload, { headers: this.headers });
    },

    validate_current_section_phone_fields() {
      const kind = this.current_profile_section?.kind;

      if (kind === 'section') {
        (this.current_profile_section?.questions || []).forEach((question) => {
          if (!this.is_phone_question_type(question.question_type)) return;
          this.on_profile_phone_blur(question, Boolean(question.is_required || question.is_mandatory));
        });
      }

      if (['language', 'education', 'reference'].includes(kind)) {
        (this.current_profile_section?.blocks || []).forEach((block) => {
          (block.fields || []).forEach((field) => {
            if (!this.is_phone_question_type(field.question_type)) return;
            this.on_profile_phone_blur(field, Boolean(field.is_required || field.is_mandatory));
          });
        });
      }

      return Object.keys(this.profile_validation_errors).length === 0;
    },

    async update_profile() {
      this.is_saving = true;
      try {
        const kind = this.current_profile_section?.kind;
        this.sync_identity_back_to_questions();

        if (!this.validate_current_section_phone_fields()) {
          throw new Error('Please fix invalid phone numbers before updating.');
        }

        if (kind === 'account') {
          await this.save_account_details();
          await this.save_password_if_provided();
          this.new_password = '';
          this.confirm_password = '';
        } else {
          await this.save_profile_answers();
        }
        this.persist_profile_cache();
        this.is_editing = false;
        this.show_success('Section updated successfully.');
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Could not update section.';
        this.show_error(msg);
      } finally {
        this.is_saving = false;
      }
    },

    open_file_preview(value, name) {
      const file_answer = extract_file_answer(value);
      const resolved_url = file_answer?.url || (typeof value === 'string' ? value : null);
      if (!resolved_url) return;

      this.file_preview_name = name || file_answer?.name || this.get_file_display_name(value);
      this.file_preview_url = resolved_url;
      this.file_preview_modal = true;
      this.$nextTick(() => {
        this.file_preview_modal = true;
      });
    },

    close_file_preview() {
      this.file_preview_modal = false;
      if (this.file_preview_url && String(this.file_preview_url).startsWith('blob:')) {
        URL.revokeObjectURL(this.file_preview_url);
      }
      this.file_preview_url = null;
      this.file_preview_name = 'Document';
    },

    clear_file(question) {
      if (question.local_file_url && String(question.local_file_url).startsWith('blob:')) {
        URL.revokeObjectURL(question.local_file_url);
      }
      question.new_file = null;
      question.local_file_url = null;
      question.editable_value = '';
    },

    handle_file_upload(event, question) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      if (this.is_pdf_file_question(question)) {
        const is_pdf = file.type === 'application/pdf' || String(file.name || '').toLowerCase().endsWith('.pdf');
        if (!is_pdf) {
          if (event?.target) {
            event.target.value = '';
          }
          this.show_error('Only PDF files are allowed for this document.');
          return;
        }
      }

      if (question.local_file_url) {
        URL.revokeObjectURL(question.local_file_url);
      }
      question.new_file = file;
      question.local_file_url = URL.createObjectURL(file);
      question.editable_value = file.name;
    },

    get_file_display_name(value) {
      if (!value) return 'Document';

      const file_answer = extract_file_answer(value);
      if (file_answer?.name) return file_answer.name;

      const url = file_answer?.url || String(value || '');
      try {
        const pathname = new URL(url).pathname;
        return decodeURIComponent(pathname.split('/').pop() || 'Document');
      } catch {
        return decodeURIComponent((url.split('?')[0].split('/').pop()) || 'Document');
      }
    },

    is_file_url(value) {
      const file_answer = extract_file_answer(value);
      if (file_answer?.url) return true;
      if (!value || typeof value !== 'string') return false;
      return value.startsWith('http') || value.startsWith('/media/') || value.includes('.s3.') || value.startsWith('blob:');
    },

    edit_profile() {
      this.$router.push('/profile_view/edit');
    },

    go_to_forms() {
      this.$router.push('/view_all_user_forms');
    },

    add_we_block() {
      const all = this._all_we_blocks || [];
      if (!all.length) return;
      if (this.work_experience_blocks.length >= all.length || this.work_experience_blocks.length >= 3) return;
      this.work_experience_blocks = [...this.work_experience_blocks, all[this.work_experience_blocks.length]];
    },

    toggle_currently_work_here_profile(block_index) {
      const block = Array.isArray(this.work_experience_blocks)
        ? this.work_experience_blocks.at(block_index)
        : null;
      if (!block) return;

      const next_value = !this.is_currently_working_block(block);

      if (next_value) {
        this.work_experience_blocks.forEach((item, idx) => {
          if (idx !== block_index) {
            item.currently_work_here = false;
          }
        });
      }

      block.currently_work_here = next_value;

      if (next_value) {
        (block.fields || []).forEach((field) => {
          const lower_label = String(field?.label || '').toLowerCase();
          if (lower_label.startsWith('to ') || lower_label.includes('reason for leaving')) {
            field.editable_value = '';
          }
        });
      }
    },

    remove_we_block(bidx) {
      if (this.work_experience_blocks.length <= 1) return;
      this.work_experience_blocks = this.work_experience_blocks.filter((_, idx) => idx !== bidx);
    },
  },
};
